/**
 * The adopted price card's maximums for the reseller's commission plans (the granted card's
 * `controls`, TMMAPI-0154 §I) in the console's own words (TXT-002: one wording): the plan editor's
 * hints (`rateLimitHint`, `coolOffLimitHint`, and the extras' `bountyLimitHint` /
 * `milestoneLimitHint`, TMMRSL-0051). The server's own limit messages are never shown; without an
 * adopted card there is no hint, and tmm-api's validation still answers. Money is in the card's
 * `currency`: a money limit is shown only on a plan in that currency; on a plan in another it says
 * which currency the limits apply in, never a converted amount (TMMRSL-0076).
 */
import type { TFunction } from 'i18next';
import type { AffiliateMaximums } from '@/api/rateCards';
import { moneyIn } from '@/components/moneyIn';
import { bpToPercentText } from '@/i18n/format';

function moneyLimitHint(
  key: 'commissionPlans.limit.bounty' | 'commissionPlans.limit.milestone',
  g: AffiliateMaximums,
  minor: number,
  planCurrency: string,
  locale: string,
  t: TFunction,
): string {
  if (g.currency !== planCurrency) {
    return t('commissionPlans.limit.otherCurrency', { currency: g.currency });
  }
  return t(key, { amount: moneyIn(minor, g.currency, locale, t('statements.unavailable')) });
}

/** "Up to 30% on your price card", for every rate field. */
export function rateLimitHint(
  g: AffiliateMaximums | null,
  locale: string,
  t: TFunction,
): string | null {
  return g ? t('commissionPlans.limit.rate', { percent: bpToPercentText(g.recurringMaxBp, locale) }) : null;
}

/** "Up to 60 days on your price card", for the cool-off. */
export function coolOffLimitHint(g: AffiliateMaximums | null, t: TFunction): string | null {
  return g ? t('commissionPlans.limit.coolOff', { count: g.coolOffMaxDays }) : null;
}

/** "Up to $100.00 on your price card", for the bounty of a plan in `planCurrency`. */
export function bountyLimitHint(
  g: AffiliateMaximums | null,
  planCurrency: string,
  locale: string,
  t: TFunction,
): string | null {
  return g
    ? moneyLimitHint('commissionPlans.limit.bounty', g, g.bountyMaxMinor, planCurrency, locale, t)
    : null;
}

/** "Up to $500.00 each on your price card", for the milestone bonuses of a plan in `planCurrency`. */
export function milestoneLimitHint(
  g: AffiliateMaximums | null,
  planCurrency: string,
  locale: string,
  t: TFunction,
): string | null {
  return g
    ? moneyLimitHint(
        'commissionPlans.limit.milestone',
        g,
        g.milestoneMaxMinor,
        planCurrency,
        locale,
        t,
      )
    : null;
}
